import React, { useContext, useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import {
  FarmRiskType,
  FarmScheduleCoverageType,
  FarmScheduleCoverageTypesByState,
  isRooflessStructureCategory,
  lookupsJson,
  policyType,
  StructureCategory
} from '@ourbranch/lookups';
import { observer } from 'mobx-react';
import { useFormikContext, getIn } from 'formik';

import { appConfig } from 'brand-config';
import { keepsMigratedRoofSettlement } from 'common/helpers/migrated-carry-over';
import { CoverageLimitOverride, isCoverageLimitLockedForUser } from 'common/components/coverage-limit-override';
import { useStore } from 'core/store';
import { useFarmPolicyContext } from 'common/hooks/use-farm-policy-context';
import { useFarmMigrationUW } from 'common/hooks/use-farm-migration-uw';
import externalLinkIcon from 'core/assets/svg/external-link.svg';
import { useToast } from 'core/components/toast';
import withDatePicker from 'core/components/with-date-picker';
import CollapsibleCard from 'core/components/collapsible-card';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { LabelTooltip } from 'core/components/label-tooltip';
import { InlandMarineForm, RelatedPrivateStructuresForm, FarmIncomeCoverageForm } from '../sub-risks';
import { Banner } from 'common/components/banner';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import { useFarmOpenSections } from 'common/hooks/farm-open-sections-context';
import { NotificationCard } from 'core/components/notification-card';
import { getDwellingHeatingOptionsForFO6, getFO6ConstructionTypeOptions } from 'common/helpers/farm-helpers';
import { Tooltip } from 'core/components/tooltip';
import editIcon from 'core/assets/svg/edit.svg';
import { AuthContext } from 'core/components/auth';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { BrandFormField } from 'common/components/farm/brand-form-field';

// Import new components and utilities
import { useFO6BusinessLogic } from './useFO6BusinessLogic';
import {
  validateRoofYear,
  getLossSettlementOptions,
  getRoofLossSettlementOptions,
  getRoofExclusionRestrictionOptions,
  validateRoofLossSettlement,
  getConfinementBuildingAge
} from './FO6.utils';
import BarnOutbuildingFields from './BarnOutbuildingFields';
import SilosGrainBinsFields from './SilosGrainBinsFields';
import ConfinementBuildingFields from './ConfinementBuildingFields';
import HoopBuildingFields from './HoopBuildingFields';
import FO6RoofFields from './FO6RoofFields';

// Import exclusion components
import {
  ExteriorWallSurfacingExclusionSection,
  CosmeticDamageUwOverride,
  useClearCosmeticDamageUwOverrideWhenAcv
} from '../exclusions';
import { COVERAGE_LIMIT_OVERRIDE_HELP_TEXT } from 'common/helpers/yup-helpers';

import useStyles from '../risk.styles';

// Form options constants
const bqOptions = lookupsJson.farm.buildingQuality;
const structureCategoriesByState = lookupsJson.farm.structureCategoriesByState;
const windHailDeductibleOptions = lookupsJson.farm.windstormOrHail;

// Note: buildingTypes will be filtered by state later in the component

const riskType = FarmRiskType.Fo6Structures;

/** Label and tooltip for the Coverage E limit field, by structure category. */
const coverageELimitCopy = ({ isFencing, isSolarPanels }) => {
  if (isFencing) {
    return {
      label: 'Fencing Coverage Limit',
      onHoverText: 'Coverage limit for fencing structures, protecting against damage from covered perils.'
    };
  }
  if (isSolarPanels) {
    return {
      label: 'Solar Panels Coverage Limit',
      onHoverText: 'Coverage limit for the solar panel array, protecting against damage from covered perils.'
    };
  }
  return {
    label: 'Scheduled Property Limit (E)',
    onHoverText: 'Coverage for buildings and structures on the property, protecting against damage from covered perils.'
  };
};

/**
 * Helper text under a structure coverage checkbox (special form, replacement cost provision, snow/ice):
 * why it is locked by the structure's loss settlement or barn class. `fallback` when nothing applies.
 */
const structureCoverageHelperText = ({
  isBarnOutbuilding,
  isConfinementOrPoultry,
  structureLossSettlement,
  buildingClass,
  class12Text,
  class3Text,
  fallback
}) => {
  if (!isBarnOutbuilding && structureLossSettlement === 'ACV') {
    return 'Not available with Actual Cash Value loss settlement';
  }
  if (isConfinementOrPoultry && structureLossSettlement === 'RC') {
    return 'Required for Replacement Cost building loss settlement';
  }
  if (isBarnOutbuilding && (buildingClass === 1 || buildingClass === 2)) {
    return class12Text;
  }
  if (isBarnOutbuilding && buildingClass === 3) {
    return class3Text;
  }
  return fallback;
};

const FO6 = ({ item, index, fieldName, removeRisk, setRiskFieldnameToEdit, canRemoveRisk, locationAddress }) => {
  const classes = useStyles();
  const { disableForm: disableRiskForm } = useActiveFormState(fieldName);
  const { isCurrentSectionOpen, toggleSection } = useFarmOpenSections(fieldName);
  const { setFieldValue, initialValues, errors, values } = useFormikContext();
  const { inPolicyRoute } = useRouteHelper();
  const store = useStore();

  const toast = useToast();
  const session = useContext(AuthContext);
  // #35617: lock Coverage E (per scheduled structure) for a non-authority user once an authorized user
  // has overridden the limit.
  const coverageLimitOverrideEnabled = Boolean(appConfig?.featureFlags?.coverageLimitOverride);
  const coverageLimitLockedForUser = isCoverageLimitLockedForUser({
    coverages: item.coverages,
    canOverrideCoverageLimit: session?.canOverrideCoverageLimit,
    coverageLimitOverrideEnabled
  });
  // #35868: while Coverage E is overridden, an authority user sees the override rules under the limit
  // instead of the standard 80%–100% band (which contradicts the value they just saved); a non-authority
  // user sees neither band nor rules — just the lock warning below.
  const coverageELimitOverridden = Boolean(item.coverages?.limitOverride);
  const showCoverageLimitOverrideRules = Boolean(session?.canOverrideCoverageLimit && coverageLimitOverrideEnabled);
  const address = locationAddress;
  // Farm product (F / Z / Y) — drives product-specific coverage gating below. #32207: resolved
  // route-aware (policy store on the policy route, where the offer store is empty) so Home (Z) /
  // Rental (Y) coverage gating applies when editing an FO-6 risk on an existing policy.
  const { farmPolicyType } = useFarmPolicyContext();
  // #34201: only underwriters servicing a migrated (imported-then-renewed) term may refresh the
  // 360Value on a structure to update Coverage E (no Coverage E rule changes — refresh only).
  const { canUwRefresh360Value } = useFarmMigrationUW();
  // Home (Z) and Rental (Y) are non-farm products and don't offer farm-only coverages (#32133).
  const isHomeOrRentalProduct = farmPolicyType === policyType.FarmHome || farmPolicyType === policyType.FarmRental;
  const hasBeenRated = !!item.id;
  const hasRceOrAcv = !!(
    item.building?.replacementCostEstimate?.total || item.building?.actualCashValueCalculation?.total
  );
  // #33448 (reopen): gate the "Adjust scheduled property limit" flow on whether the offer has been
  // RE-RATED, not on item.id. The reopen root cause: the risk is assigned its id AND its RCE by the
  // very first "See price" rate, so `hasBeenRated` (item.id) is already true on the pre-re-rate
  // offer and the previous lock never engaged. On that initial offer version the next "Update Offer
  // Price" finalizes the server limit — coverageE snaps to 100% RCE and the toggle clears — so any
  // limit + justification entered beforehand is silently discarded. The offer-level re-rate signal
  // is `parentId`: the initial See-price version has none (updateCount 1), a re-rated child offer
  // has one (updateCount ≥ 2). An existing bound policy (policy route) is already stable, so it's
  // treated as re-rated. Confirmed against the two real Forreston repro offers.
  const offerHasBeenReRated = inPolicyRoute || !!store.offer?.offer?.parentId;
  const state = address?.state;
  const ctOptions = getFO6ConstructionTypeOptions(state);
  const availableCoverageTypes =
    appConfig.farm[state]?.risks[riskType]?.allowedOptions?.scheduledCoverageTypes ||
    FarmScheduleCoverageTypesByState[state] ||
    [];
  // Get Mine Subsidence coverage limit from appConfig (GitHub issue #28634)
  const mineSubsidenceCoverageLimit = appConfig.farm?.[state]?.coverageLimits?.mineSubsidenceCoverageLimit || 750000;

  // Get building type from the item
  const buildingType = item.building?.buildingType;
  const availableStructureCategories = state ? structureCategoriesByState[state] || [] : [];
  const buildingTypeLabel = availableStructureCategories.find((option) => option.id === buildingType)?.value;

  // Use normal dwelling heating options for underwriting (FO-1/FO-2/FO-3 style)
  // These will be mapped to agricultural codes when sending to 360Value/Verisk
  const currentHeatingOptions = getDwellingHeatingOptionsForFO6();

  const isSelectedHeatingSourceUnitCountBased =
    currentHeatingOptions.find((option) => option.id === item.building?.primaryHeatingSource)?.unitCountBased ?? false;

  // Check field requirements based on occupancy type
  const isTowerOccupancy = item.building?.occupancy === 'GHT';
  const isBulkBinHopper = item.building?.occupancy === 'GHH';
  const isGrainBin = item.building?.occupancy === 'GHB';
  const shouldEnableDiameterHeight = isTowerOccupancy || isBulkBinHopper || isGrainBin;
  const shouldEnableSqFt = item.building?.occupancy && !isTowerOccupancy;

  // New BO occupancy types with specific business logic
  const isHayBarn = item.building?.occupancy === 'HB';

  const riskCoverages = getIn(values, fieldName)?.coverages || {};
  const initialCoverageE = getIn(initialValues, `${fieldName}.coverages.coverageE`);

  // Use the custom hook for business logic
  const {
    // Structure type flags
    isBarnOutbuilding,
    isPoultryBuilding,
    isDwellingContents,
    isFencing,
    isOpenSidedBuilding,
    isQuonsetBuilding,
    isHoopBuilding,

    // Barn business logic
    buildingClass,
    isRequiredFieldForClass,
    type1EligibilityValidation,

    // Validation and options
    coverageValidation,

    // Refs
    coverageHasBeenUpdatedRef
  } = useFO6BusinessLogic({
    item,
    fieldName,
    setFieldValue,
    buildingType,
    initialCoverageE,
    riskType,
    state,
    values,
    isUnderwriter: Boolean(session.canApproveUWReview)
  });

  // Additional structure type flags not in the hook
  const isStorageGrain = buildingType === StructureCategory.SilosGrainBins;
  const isConfinementBuilding = buildingType === StructureCategory.ConfinementBuildings;
  const isSolarPanels = buildingType === StructureCategory.SolarPanels;
  // #33790: Solar Panels (MO-only) rate off an operator-entered limit with no 360Value RCE backfill,
  // so the Coverage E section treats them exactly like Fencing — a simple editable limit + deductibles,
  // with none of the RCE-adjust band / "adjust scheduled property limit" machinery that only applies to
  // structures that carry a 360Value estimate.
  const isLimitBasedStructure = isFencing || isSolarPanels;

  // #34328 (CFM): Earthquake is not offered on Solar Panels or Type 3 (Building Class 3) barns —
  // we do not provide earthquake for those two items. Hide the Earthquake Deductible field for them
  // (and clear any stale value below so it can't reach rating). Type 1/2 barns and every other
  // structure category keep the field.
  const isType3Barn = isBarnOutbuilding && buildingClass === 3;
  const hideEarthquakeDeductible = isSolarPanels || isType3Barn;

  // #33841 (CFM) / #33849 (Forreston): Hoop Buildings, Silos/Grain Bins, Solar Panels and Fencing
  // have no roof, so Roof Loss Settlement and the roof exclusion/restriction don't apply — hidden
  // here and suppressed on the dec (docs-engine farm-data-mapper `hasRoofLossSettlement`). This is a
  // superset of isLimitBasedStructure (which is about the Coverage E limit UI, Fencing/Solar only).
  const isRooflessStructure = isRooflessStructureCategory(buildingType);

  // Building age calculation for age-based RC eligibility (confinement and poultry buildings)
  // GitHub issue #28945
  const buildingAge = useMemo(() => getConfinementBuildingAge(item.building?.yearBuilt), [item.building?.yearBuilt]);
  const ageThresholdBusinessRules = appConfig?.farm?.[state]?.risks[riskType]?.businessRules;
  let buildingMaxAgeThreshold = 15;
  if (isConfinementBuilding) {
    buildingMaxAgeThreshold = ageThresholdBusinessRules?.confinementBuildingMaxAgeYears ?? 15;
  } else if (isPoultryBuilding) {
    buildingMaxAgeThreshold = ageThresholdBusinessRules?.poultryBuildingMaxAgeYears ?? 15;
  }

  // Roof year validation for Poultry buildings
  const [poultryRoofYearError, setPoultryRoofYearError] = useState('');
  // #35870: switching an overridden structure to an ACV settlement withdraws the cosmetic damage override.
  useClearCosmeticDamageUwOverrideWhenAcv({ fieldName: `${fieldName}.coverages`, coverages: item.coverages });

  // Coverage E band copy under the limit field.
  const coverageEDescription = () => {
    if (coverageELimitOverridden) {
      // #35868: override rules replace the band copy for an authority user; a non-authority
      // user gets no band (the lock warning below says why the field is read-only).
      return showCoverageLimitOverrideRules && coverageValidation.isValid
        ? COVERAGE_LIMIT_OVERRIDE_HELP_TEXT
        : undefined;
    }
    if (
      isLimitBasedStructure ||
      !item.coverages?.canEditCoverageAmount ||
      getIn(errors, `${fieldName}.coverages.coverageE`)
    ) {
      return undefined;
    }
    const rceTotal = item.building?.replacementCostEstimate?.total;
    if (item.coverages?.structureLossSettlement === 'ACV' && item.building?.actualCashValueCalculation?.total) {
      const upTo = rceTotal ? ` to $${rceTotal.toLocaleString()} (100% RCE)` : '';
      return `You may adjust this from $${Math.round(item.building.actualCashValueCalculation.total * 0.8).toLocaleString()} (80% ACV)${upTo}.`;
    }
    // #33448: the RC band must be computed off the true RCE, not the current limit
    // (initialCoverageE). CFM defaults the limit below 100% RCE, so basing the copy on
    // the limit advertised a floor beneath the one validate80PercentRC enforces.
    if (rceTotal) {
      return `You may adjust this from $${(rceTotal * 0.8).toLocaleString()} (80% RCE) to $${rceTotal.toLocaleString()} (100% RCE).`;
    }
    return `You may adjust this from $${(initialCoverageE * 0.8).toLocaleString()} (80% RCE) to $${initialCoverageE.toLocaleString()} (100% RCE).`;
  };

  const coverageEHelperText = () => {
    if (coverageELimitOverridden) {
      // #35868: only the inline validator's $1,000 floor error surfaces here.
      return coverageValidation.isValid ? undefined : coverageValidation.message;
    }
    const showsBand = item.coverages?.canEditCoverageAmount && !getIn(errors, `${fieldName}.coverages.coverageE`);
    return !isLimitBasedStructure && coverageValidation.message && !showsBand ? coverageValidation.message : undefined;
  };

  // #37110: a migrated structure still holding the roof settlement and restriction GO wrote keeps them (the farm
  // post-data rules keep them too); changing its category or restriction re-enables the rules below. #37629: the roof
  // field doesn't flag it either.
  const keepsMigratedRoof = keepsMigratedRoofSettlement(item);

  // Roof loss settlement validation
  const roofLossSettlementValidation = validateRoofLossSettlement(item.coverages, { keepsMigratedRoof });
  const [currentPoultryRoofYear, setCurrentPoultryRoofYear] = useState(item.building?.roofYear);
  const [currentPoultryYearBuilt, setCurrentPoultryYearBuilt] = useState(item.building?.yearBuilt);

  // Memoize roof loss settlement options to fix FastField rendering issue
  const roofLossSettlementOptions = useMemo(() => {
    return getRoofLossSettlementOptions({
      structureLossSettlement: item.coverages?.structureLossSettlement,
      buildingClass,
      isBarnOutbuilding,
      isConfinementBuilding,
      isPoultryBuilding,
      roofExclusionRestriction: item.coverages?.roofExclusionRestriction
    });
  }, [
    item.coverages?.structureLossSettlement,
    buildingClass,
    isBarnOutbuilding,
    isConfinementBuilding,
    isPoultryBuilding,
    item.coverages?.roofExclusionRestriction
  ]);

  // #33576: Only offer "Roof Actual Cash Value" when the roof would otherwise be Replacement Cost.
  // Type 3 barns and ACV structures already settle the roof at ACV, so only "Roof Excluded" applies.
  const roofExclusionRestrictionOptions = useMemo(() => {
    return getRoofExclusionRestrictionOptions({
      isBarnOutbuilding,
      buildingClass,
      structureLossSettlement: item.coverages?.structureLossSettlement
    });
  }, [isBarnOutbuilding, buildingClass, item.coverages?.structureLossSettlement]);

  // Sync local state with Formik values for Poultry buildings
  useEffect(() => {
    if (isPoultryBuilding) {
      const formikRoofYear = values[fieldName]?.building?.roofYear;
      const formikYearBuilt = values[fieldName]?.building?.yearBuilt;

      if (formikRoofYear !== currentPoultryRoofYear) {
        setCurrentPoultryRoofYear(formikRoofYear);
      }
      if (formikYearBuilt !== currentPoultryYearBuilt) {
        setCurrentPoultryYearBuilt(formikYearBuilt);
      }
    }
  }, [values, fieldName, isPoultryBuilding, currentPoultryRoofYear, currentPoultryYearBuilt]);

  // Validate Poultry roof year when roofYear or yearBuilt changes
  useEffect(() => {
    if (isPoultryBuilding) {
      const validation = validateRoofYear(currentPoultryRoofYear, currentPoultryYearBuilt);
      setPoultryRoofYearError(validation.isValid ? '' : validation.message);
    }
  }, [currentPoultryRoofYear, currentPoultryYearBuilt, isPoultryBuilding]);

  // Roof year validation for Dwelling buildings
  const [dwellingRoofYearError, setDwellingRoofYearError] = useState('');
  const [currentDwellingRoofYear, setCurrentDwellingRoofYear] = useState(item.building?.roofYear);
  const [currentDwellingYearBuilt, setCurrentDwellingYearBuilt] = useState(item.building?.yearBuilt);

  // Sync local state with Formik values for Dwelling buildings
  useEffect(() => {
    if (isDwellingContents) {
      const formikRoofYear = values[fieldName]?.building?.roofYear;
      const formikYearBuilt = values[fieldName]?.building?.yearBuilt;

      if (formikRoofYear !== currentDwellingRoofYear) {
        setCurrentDwellingRoofYear(formikRoofYear);
      }
      if (formikYearBuilt !== currentDwellingYearBuilt) {
        setCurrentDwellingYearBuilt(formikYearBuilt);
      }
    }
  }, [values, fieldName, isDwellingContents, currentDwellingRoofYear, currentDwellingYearBuilt]);

  // Validate Dwelling roof year when roofYear or yearBuilt changes
  useEffect(() => {
    if (isDwellingContents) {
      const validation = validateRoofYear(currentDwellingRoofYear, currentDwellingYearBuilt);
      setDwellingRoofYearError(validation.isValid ? '' : validation.message);
    }
  }, [currentDwellingRoofYear, currentDwellingYearBuilt, isDwellingContents]);

  // Auto-enable roof exclusion/restriction toggle for existing data
  const hasAutoEnabledRoofRestrictionRef = useRef(false);
  useEffect(() => {
    const currentRestriction = item.coverages?.roofExclusionRestriction;
    const currentToggle = item.coverages?.hasRoofExclusionRestriction;

    // Auto-enable toggle if restriction data exists but toggle is undefined (only once).
    // Skip roofless structures (#33841/#33849) — the toggle is hidden there, so don't resurrect
    // stale roof-exclusion data onto a category that has no roof.
    if (
      !isRooflessStructure &&
      currentRestriction &&
      currentRestriction !== 'none' &&
      currentToggle === undefined &&
      !hasAutoEnabledRoofRestrictionRef.current
    ) {
      setFieldValue(`${fieldName}.coverages.hasRoofExclusionRestriction`, true);
      hasAutoEnabledRoofRestrictionRef.current = true;
    }
  }, [item, fieldName, setFieldValue, isRooflessStructure]);

  // #33841/#33849: when a structure is (or becomes) roofless, drop any persisted roof
  // exclusion/restriction. Its fields are now hidden, so a stale toggle would otherwise (a) strand
  // the operator on a required-description validation they can't see, and (b) feed a roof exclusion
  // into ClarionDoor rating for a category with no roof (the farm rater sends roof_exclusion for
  // every FO6 risk). The backend post-data-rules is the authoritative guard; this keeps the in-form
  // coverages consistent with what's displayed. Guarded so it's a no-op (no spurious form-dirty /
  // re-rate) for the common case with no stale data.
  // #37110: except on a migrated structure still holding the roof settlement and restriction GO wrote
  // (the farm post-data rules keep them too). Changing its category or restriction re-enables this.
  useEffect(() => {
    if (!isRooflessStructure || keepsMigratedRoof) {
      return;
    }
    if (item.coverages?.hasRoofExclusionRestriction) {
      setFieldValue(`${fieldName}.coverages.hasRoofExclusionRestriction`, false);
    }
    if (item.coverages?.roofExclusionRestriction && item.coverages.roofExclusionRestriction !== 'none') {
      setFieldValue(`${fieldName}.coverages.roofExclusionRestriction`, null);
    }
  }, [
    isRooflessStructure,
    keepsMigratedRoof,
    fieldName,
    item.coverages?.hasRoofExclusionRestriction,
    item.coverages?.roofExclusionRestriction,
    setFieldValue
  ]);

  // #34328: when a structure is (or becomes) a Solar Panels or Type 3 barn, drop any elected
  // earthquake deductible. The field is now hidden, so a stale value would otherwise feed EQ into
  // ClarionDoor rating (the farm rater sends covE_eq_deductible for every FO6 risk). Clear to 0
  // ("No coverage"), matching the Zone-4 post-data-rule. The backend post-data-rules is the
  // authoritative guard; this keeps the in-form coverages consistent with what's displayed. Guarded
  // so it's a no-op (no spurious form-dirty / re-rate) for the common case with no elected EQ.
  useEffect(() => {
    if (!hideEarthquakeDeductible) {
      return;
    }
    if (item.coverages?.deductibleEarthquake) {
      setFieldValue(`${fieldName}.coverages.deductibleEarthquake`, 0);
    }
  }, [hideEarthquakeDeductible, fieldName, item.coverages?.deductibleEarthquake, setFieldValue]);

  const initialBuilding = getIn(initialValues, `${fieldName}.building`);

  const toastRCEWarning = () => {
    if (coverageHasBeenUpdatedRef.current) {
      toast.notify({
        type: 'success',
        message:
          'Changing this item may affect the dwelling limit. After updating the offer, review the dwelling limit to ensure it is correct.'
      });
    }
  };

  return (
    <CollapsibleCard
      content={
        <div className={classes.header}>
          <div className={classes.headerRow}>
            <Label type="infoTitleSmall">FO6 Structure: {buildingTypeLabel}</Label>
            <div className={classes.divider} />
            <Label type="infoSubtitleSmall">{item.description}</Label>
            <IconButton onClick={() => setRiskFieldnameToEdit(fieldName)} className={classes.editButton}>
              <img className={classes.editIcon} src={editIcon} alt="Edit row" />
            </IconButton>
          </div>
        </div>
      }
      isRemovable={canRemoveRisk}
      onDelete={() => removeRisk(index)}
      disableRemoveButton={disableRiskForm}
      open={isCurrentSectionOpen}
      onChange={toggleSection}
      id={fieldName}
    >
      {!hasBeenRated && (
        <Banner
          variant="warning"
          title="Data for this dwelling or structure isn't available—please enter the details manually."
          subtitle={
            inPolicyRoute
              ? "This information won't auto-fill, even if you preview changes."
              : "This information won't auto-fill, even if you update the offer."
          }
        />
      )}
      {buildingClass === 1 && !type1EligibilityValidation.isValid && (
        <Banner
          variant="error"
          title="Building Class 1 Requirements Not Met"
          subtitle={`${type1EligibilityValidation.message} Please select Building Class 2 or 3, or fix the building characteristics.`}
        />
      )}
      <Grid container className={classes.riskCard} spacing={2}>
        <Grid item xs={12}>
          <Label type="infoTitleSmall">Property</Label>
        </Grid>

        {isBarnOutbuilding && (
          <BarnOutbuildingFields
            fieldName={fieldName}
            buildingType={buildingType}
            farmPolicyType={farmPolicyType}
            initialBuilding={initialBuilding}
            disableRiskForm={disableRiskForm}
            currentHeatingOptions={currentHeatingOptions}
            isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
            isOpenSidedBuilding={isOpenSidedBuilding}
            isQuonsetBuilding={isQuonsetBuilding}
            isHayBarn={isHayBarn}
            item={item}
            toastRCEWarning={toastRCEWarning}
            ctOptions={ctOptions}
          />
        )}

        {isStorageGrain && (
          <SilosGrainBinsFields
            fieldName={fieldName}
            buildingType={buildingType}
            initialBuilding={initialBuilding}
            disableRiskForm={disableRiskForm}
            shouldEnableDiameterHeight={shouldEnableDiameterHeight}
            shouldEnableSqFt={shouldEnableSqFt}
            toastRCEWarning={toastRCEWarning}
            ctOptions={ctOptions}
            occupancy={item.building?.occupancy}
          />
        )}

        {isPoultryBuilding && (
          <>
            <Field
              xs={3}
              type="numeric"
              id={`${fieldName}.building.yearBuilt`}
              name={`${fieldName}.building.yearBuilt`}
              mode="light"
              label="Year built in"
              format={{
                allowNegative: false,
                isAllowed: ({ value }) => value.length <= 4
              }}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.yearBuilt) {
                  toastRCEWarning();
                }
                setCurrentPoultryYearBuilt(val);
                setFieldValue(`${fieldName}.building.yearBuilt`, val);
              }}
            />
            <Field
              xs={3}
              type="numeric"
              id={`${fieldName}.building.roofYear`}
              name={`${fieldName}.building.roofYear`}
              mode="light"
              label="Roof year"
              format={{
                allowNegative: false,
                isAllowed: ({ value }) => value.length <= 4
              }}
              disabled={disableRiskForm}
              error={!!poultryRoofYearError || !!getIn(errors, `${fieldName}.building.roofYear`)}
              helperText={poultryRoofYearError || getIn(errors, `${fieldName}.building.roofYear`) || undefined}
              onChange={(val) => {
                if (val !== initialBuilding.roofYear) {
                  toastRCEWarning();
                }
                setCurrentPoultryRoofYear(val);
                setFieldValue(`${fieldName}.building.roofYear`, val);
              }}
            />
            <Field
              xs={4}
              type="numeric"
              id={`${fieldName}.building.sqFt`}
              name={`${fieldName}.building.sqFt`}
              mode="light"
              label="Total sqft"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.sqFt) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={3}
              type="select"
              id={`${fieldName}.building.buildingQuality`}
              name={`${fieldName}.building.buildingQuality`}
              mode="light"
              label="Quality"
              options={bqOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.buildingQuality) {
                  toastRCEWarning();
                }
              }}
            />
            <FO6RoofFields
              fieldName={fieldName}
              initialBuilding={initialBuilding}
              disabled={disableRiskForm}
              shapeXs={3}
              materialXs={3}
              onShapeChange={toastRCEWarning}
            />
            <Field
              xs={6}
              type="select"
              id={`${fieldName}.building.constructionType`}
              name={`${fieldName}.building.constructionType`}
              mode="light"
              label="Construction type"
              options={ctOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.constructionType) {
                  toastRCEWarning();
                }
              }}
            />
            <Grid item xs={4}>
              <LabelTooltip
                label="Solid Fuel Burning Appliances"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'Solid Fuel or wood heat examples include standard fireplace, fireplace with insert, indoor solid fuel or wood heat (including pellet stoves), outside solid fuel or wood heat. Barrel or Homemade stoves are Not Acceptable.'
                }}
                mode="light"
              >
                <Field
                  type="numeric"
                  id={`${fieldName}.building.numSolidFuelBurningAppliances`}
                  name={`${fieldName}.building.numSolidFuelBurningAppliances`}
                  mode="light"
                  disabled={disableRiskForm}
                  format={{
                    allowNegative: false
                  }}
                  onChange={(val) => {
                    if (val !== initialBuilding.numSolidFuelBurningAppliances) {
                      toastRCEWarning();
                    }
                  }}
                />
              </LabelTooltip>
            </Grid>
            <Field
              xs={4}
              type="select"
              id={`${fieldName}.building.primaryHeatingSource`}
              name={`${fieldName}.building.primaryHeatingSource`}
              mode="light"
              label="Primary heating"
              options={currentHeatingOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.primaryHeatingSource) {
                  toastRCEWarning();
                }
              }}
            />
          </>
        )}

        {isFencing && (
          <Field
            xs={6}
            type="numeric"
            id={`${fieldName}.building.yearBuilt`}
            name={`${fieldName}.building.yearBuilt`}
            mode="light"
            label="Year built in"
            format={{
              allowNegative: false,
              isAllowed: ({ value }) => value.length <= 4
            }}
            disabled={disableRiskForm}
          />
        )}

        {isSolarPanels && (
          <>
            <Field
              xs={4}
              type="numeric"
              id={`${fieldName}.building.yearBuilt`}
              name={`${fieldName}.building.yearBuilt`}
              mode="light"
              label="Year built in"
              format={{
                allowNegative: false,
                isAllowed: ({ value }) => value.length <= 4
              }}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.yearBuilt) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="numeric"
              id={`${fieldName}.building.numSolarPanels`}
              name={`${fieldName}.building.numSolarPanels`}
              mode="light"
              label="Number of panels"
              format={{
                allowNegative: false
              }}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.numSolarPanels) {
                  toastRCEWarning();
                }
              }}
            />
            <Grid item xs={4} />
            <Grid item xs={12}>
              <Label type="infoTitleSmall">Mounted on</Label>
            </Grid>
            <Field
              xs={4}
              type="checkbox"
              id={`${fieldName}.building.solarMountDwelling`}
              name={`${fieldName}.building.solarMountDwelling`}
              mode="light"
              label="Dwelling"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.solarMountDwelling) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="checkbox"
              id={`${fieldName}.building.solarMountOutbuilding`}
              name={`${fieldName}.building.solarMountOutbuilding`}
              mode="light"
              label="Outbuilding"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.solarMountOutbuilding) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="checkbox"
              id={`${fieldName}.building.solarMountGround`}
              name={`${fieldName}.building.solarMountGround`}
              mode="light"
              label="Ground"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.solarMountGround) {
                  toastRCEWarning();
                }
              }}
            />
          </>
        )}

        {isConfinementBuilding && (
          <ConfinementBuildingFields
            fieldName={fieldName}
            buildingType={buildingType}
            initialBuilding={initialBuilding}
            disableRiskForm={disableRiskForm}
            currentHeatingOptions={currentHeatingOptions}
            isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
            toastRCEWarning={toastRCEWarning}
            ctOptions={ctOptions}
          />
        )}

        {isHoopBuilding && (
          <HoopBuildingFields
            fieldName={fieldName}
            initialBuilding={initialBuilding}
            disableRiskForm={disableRiskForm}
            currentHeatingOptions={currentHeatingOptions}
            isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
            toastRCEWarning={toastRCEWarning}
            ctOptions={ctOptions}
          />
        )}

        {isDwellingContents && (
          <>
            <Field
              xs={6}
              type="select"
              id={`${fieldName}.building.residenceType`}
              name={`${fieldName}.building.residenceType`}
              mode="light"
              label="Residence type/usage"
              options={[
                { id: 'rental', value: 'Rental' },
                { id: 'seasonal', value: 'Seasonal' },
                { id: 'vacant', value: 'Vacant' }
              ]}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.residenceType) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={3}
              type="numeric"
              id={`${fieldName}.building.yearBuilt`}
              name={`${fieldName}.building.yearBuilt`}
              mode="light"
              label="Year built in"
              format={{
                allowNegative: false,
                isAllowed: ({ value }) => value.length <= 4
              }}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.yearBuilt) {
                  toastRCEWarning();
                }
                setCurrentDwellingYearBuilt(val);
                setFieldValue(`${fieldName}.building.yearBuilt`, val);
              }}
            />
            <Field
              xs={3}
              type="numeric"
              id={`${fieldName}.building.roofYear`}
              name={`${fieldName}.building.roofYear`}
              mode="light"
              label="Roof year"
              format={{
                allowNegative: false,
                isAllowed: ({ value }) => value.length <= 4
              }}
              disabled={disableRiskForm}
              error={!!getIn(errors, `${fieldName}.building.roofYear`) || !!dwellingRoofYearError}
              helperText={getIn(errors, `${fieldName}.building.roofYear`) || dwellingRoofYearError || undefined}
              onChange={(val) => {
                if (val !== initialBuilding.roofYear) {
                  toastRCEWarning();
                }
                setCurrentDwellingRoofYear(val);
                setFieldValue(`${fieldName}.building.roofYear`, val);
              }}
            />
            <Field
              xs={3}
              type="numeric"
              id={`${fieldName}.building.sqFt`}
              name={`${fieldName}.building.sqFt`}
              mode="light"
              label="Total sqft"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.sqFt) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={3}
              type="select"
              id={`${fieldName}.building.buildingQuality`}
              name={`${fieldName}.building.buildingQuality`}
              mode="light"
              label="Quality"
              options={bqOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.buildingQuality) {
                  toastRCEWarning();
                }
              }}
            />
            <FO6RoofFields
              fieldName={fieldName}
              initialBuilding={initialBuilding}
              disabled={disableRiskForm}
              shapeXs={4}
              materialXs={4}
              onShapeChange={toastRCEWarning}
            />
            <Field
              xs={4}
              type="select"
              id={`${fieldName}.building.constructionType`}
              name={`${fieldName}.building.constructionType`}
              mode="light"
              label="Construction type"
              options={ctOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.constructionType) {
                  toastRCEWarning();
                }
              }}
            />
            <Grid item xs={4}>
              <LabelTooltip
                label="Solid Fuel Burning Appliances"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'Solid Fuel or wood heat examples include standard fireplace, fireplace with insert, indoor solid fuel or wood heat (including pellet stoves), outside solid fuel or wood heat. Barrel or Homemade stoves are Not Acceptable.'
                }}
                mode="light"
              >
                <Field
                  type="numeric"
                  id={`${fieldName}.building.numSolidFuelBurningAppliances`}
                  name={`${fieldName}.building.numSolidFuelBurningAppliances`}
                  mode="light"
                  disabled={disableRiskForm}
                  format={{
                    allowNegative: false
                  }}
                  onChange={(val) => {
                    if (val !== initialBuilding.numSolidFuelBurningAppliances) {
                      toastRCEWarning();
                    }
                  }}
                />
              </LabelTooltip>
            </Grid>
            <Field
              xs={4}
              type="select"
              id={`${fieldName}.building.primaryHeatingSource`}
              name={`${fieldName}.building.primaryHeatingSource`}
              mode="light"
              label="Primary heating"
              options={currentHeatingOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.primaryHeatingSource) {
                  toastRCEWarning();
                }
              }}
            />
          </>
        )}

        {(hasBeenRated || coverageHasBeenUpdatedRef.current) && !isLimitBasedStructure && !isDwellingContents && (
          <NotificationCard className={classes.notification} iconSize="icon16" type="infoGreen">
            <Grid container className={classes.rceLabel}>
              <Label>Our estimated cost to replace this structure</Label>
              <Tooltip
                label="More info"
                text="RCE (Replacement Cost Estimate) and ACV (Actual Cash Value) are calculated using 360Value. RCE is the cost to replace the structure with new materials. ACV accounts for depreciation based on age and condition. The selected loss settlement option determines which value applies to the claim."
              />
            </Grid>
            <Grid container item xs={4} spacing={2} justifyContent="flex-end">
              <Grid item className={classes.rceValues}>
                <Label>
                  RCE
                  {item.coverages?.structureLossSettlement === 'RC' ||
                  item.coverages?.structureLossSettlement === 'FUNC' ||
                  item.coverages?.roofLossSettlement === 'RC_ACV_WIND_HAIL'
                    ? ' (Active)'
                    : ''}
                </Label>
                <Label type="infoValueBold">
                  {item.building?.replacementCostEstimate?.total
                    ? `$${item.building.replacementCostEstimate.total.toLocaleString('en-US')}`
                    : 'N/A'}
                </Label>
              </Grid>
              {item.building?.actualCashValueCalculation?.total && (
                <Grid item className={classes.rceValues}>
                  <Label>
                    ACV
                    {item.coverages?.structureLossSettlement === 'ACV' ? ' (Active)' : ''}
                  </Label>
                  <Label type="infoValueBold">
                    ${item.building.actualCashValueCalculation.total.toLocaleString('en-US')}
                  </Label>
                </Grid>
              )}
            </Grid>
          </NotificationCard>
        )}
      </Grid>

      {(isDwellingContents || isLimitBasedStructure || hasBeenRated || hasRceOrAcv) && (
        <Grid container spacing={2} className={classes.coverageCard}>
          {isDwellingContents && (
            <>
              <Grid item xs={12} className={classes.bottomMargin}>
                <Label type="formSubTitle">Base coverages</Label>
              </Grid>
              <Grid item xs={6}>
                <LabelTooltip
                  label="Coverage E - Dwelling Limit"
                  tooltip={{
                    label: 'More Info',
                    onHoverText:
                      'Coverage limit for the dwelling structure itself, protecting against damage from covered perils.'
                  }}
                >
                  <Field
                    id={`${fieldName}.coverages.coverageE`}
                    name={`${fieldName}.coverages.coverageE`}
                    type="numeric"
                    mode="dark"
                    // #31830/#31831: FastField (the default) drops the dwelling limit edit on the
                    // first DC structure when a data-pull/reinitialize re-renders the form mid-edit,
                    // so it reverts to the $20,000 default. Force a regular Field so the entered
                    // value always commits — matching the rated Coverage E field below.
                    fast={false}
                    format={{ thousandSeparator: true, prefix: '$' }}
                    disabled={disableRiskForm || coverageLimitLockedForUser}
                  />
                </LabelTooltip>
              </Grid>
              <Grid item xs={6}>
                <LabelTooltip
                  label="Coverage E - Contents Limit"
                  tooltip={{
                    label: 'More Info',
                    onHoverText:
                      'Coverage limit for contents within the dwelling, protecting personal property from covered perils.'
                  }}
                >
                  <Field
                    id={`${fieldName}.coverages.contentsLimit`}
                    name={`${fieldName}.coverages.contentsLimit`}
                    type="numeric"
                    mode="dark"
                    // #31830/#31831: see Coverage E above — keep the contents limit on a regular
                    // Field so it doesn't revert to its default the same way.
                    fast={false}
                    format={{ thousandSeparator: true, prefix: '$' }}
                    disabled={disableRiskForm}
                    defaultValue={0}
                    error={(riskCoverages.contentsLimit ?? 0) > (riskCoverages.coverageE ?? 0)}
                    helperText={
                      (riskCoverages.contentsLimit ?? 0) > (riskCoverages.coverageE ?? 0)
                        ? 'Contents Limit cannot exceed Dwelling Limit'
                        : undefined
                    }
                  />
                </LabelTooltip>
              </Grid>
            </>
          )}
          {!isDwellingContents && (isLimitBasedStructure || hasBeenRated || hasRceOrAcv) && (
            <>
              <Grid item xs={12} className={classes.bottomMargin}>
                <Label type="formSubTitle">Base coverages</Label>
              </Grid>
              <Grid item xs={12}>
                <LabelTooltip
                  label={coverageELimitCopy({ isFencing, isSolarPanels }).label}
                  tooltip={{
                    label: 'More Info',
                    onHoverText: coverageELimitCopy({ isFencing, isSolarPanels }).onHoverText
                  }}
                >
                  <Field
                    id={`${fieldName}.coverages.coverageE`}
                    name={`${fieldName}.coverages.coverageE`}
                    type="numeric"
                    mode="dark"
                    fast={false}
                    format={{ thousandSeparator: true, prefix: '$' }}
                    disabled={disableRiskForm || coverageLimitLockedForUser}
                    description={coverageEDescription()}
                    helperText={coverageEHelperText()}
                    error={!isLimitBasedStructure && !coverageValidation.isValid}
                  />
                </LabelTooltip>
              </Grid>

              {!isLimitBasedStructure && (
                <div className={classes.coverageToggleContainer}>
                  <Grid container justifyContent="space-between">
                    <Field
                      id={`${fieldName}.coverages.canEditCoverageAmount`}
                      name={`${fieldName}.coverages.canEditCoverageAmount`}
                      type="switch"
                      mode="dark"
                      label="Adjust scheduled property limit"
                      tooltipText="By default (and when toggled off), the scheduled property limit matches our estimated replacement cost. If you have an external estimate, you can adjust the limit here. Supporting documentation will be required."
                      tooltipLabel="More Info"
                      xs={8}
                      // #33448: lock the adjust control until the offer has been re-rated once
                      // (offerHasBeenReRated). Before the first re-rate the server hasn't finalized the
                      // limit, so any limit/justification entered here is silently discarded when the first
                      // re-rate snaps the limit to 100% RCE and clears the toggle. Locking prevents that lost
                      // input. (item.id is set by the first See-price rate, so it can't gate the *re-rate*.)
                      // #35617: also locked for a non-authority user once the limit is overridden.
                      disabled={coverageHasBeenUpdatedRef.current || !offerHasBeenReRated || coverageLimitLockedForUser}
                    />
                    <Grid className={classes.linkAndIcon}>
                      <a
                        rel="noreferrer noopener"
                        target="_blank"
                        href="https://www.360-value.com/apps/iv/ui/#/login"
                        className={classes.link}
                      >
                        ISO 360 Login
                        <img src={externalLinkIcon} alt="link to ISO 360 login" />
                      </a>
                    </Grid>

                    {offerHasBeenReRated && item.coverages?.canEditCoverageAmount && (
                      <Grid item xs={12} className={classes.adjustCoverageSubContainer}>
                        <Field
                          id={`${fieldName}.coverages.adjustDwellingLimitDescription`}
                          name={`${fieldName}.coverages.adjustDwellingLimitDescription`}
                          type="textarea"
                          mode="dark"
                          label="Description of reason for adjusting scheduled property limit (required)"
                          xs={12}
                          rows={3}
                        />
                      </Grid>
                    )}

                    {/* #34201: underwriter-only, migrated terms only. When ON, the next re-rate
                        re-pulls the 360Value to refresh Coverage E. Coverage E keeps its normal
                        floors/caps (refresh only — no rule bypass). */}
                    {canUwRefresh360Value && (
                      <Field
                        id={`${fieldName}.coverages.uwRefresh360Value`}
                        name={`${fieldName}.coverages.uwRefresh360Value`}
                        type="switch"
                        mode="dark"
                        label="Underwriter: refresh 360Value"
                        tooltipText="Migrated policies keep their carried-over scheduled property limit. Turn this on (underwriters only) to re-run 360Value on the next rate and refresh Coverage E. Coverage E limits remain subject to their normal rules."
                        tooltipLabel="More Info"
                        xs={8}
                        disabled={coverageHasBeenUpdatedRef.current || !offerHasBeenReRated}
                      />
                    )}

                    {/* #35617: authority-gated Coverage E limit override (locks the limit). Follows the
                        same re-rate enable rule as the adjust toggle above (don't loosen #33448 gating).
                        #35867: but NOT the adjust toggle's `coverageHasBeenUpdatedRef` guard — that ref
                        flips true as soon as the UW types the new limit, which disabled the required
                        reason textarea (and the switch) mid-flow and left the structure stuck locked. */}
                    <CoverageLimitOverride
                      fieldName={`${fieldName}.coverages`}
                      coverageLabel="Coverage E"
                      coverages={item.coverages}
                      disabled={disableRiskForm || !offerHasBeenReRated}
                    />

                    {coverageLimitLockedForUser && (
                      <Label type="infoLabel" className={classes.coverageLimitLockWarning}>
                        The Coverage E limit is locked. Please reach out to underwriting to make changes.
                      </Label>
                    )}
                  </Grid>
                </div>
              )}
            </>
          )}

          {(isLimitBasedStructure || hasBeenRated || hasRceOrAcv) && (
            <>
              <Grid item xs={12} className={classes.bottomMargin}>
                <Label type="formSubTitle" className={classes.sectionDivider}>
                  Deductibles
                </Label>
              </Grid>
              <BrandFormField
                xs={4}
                id={`${fieldName}.coverages.deductibleAllOther`}
                name={`${fieldName}.coverages.deductibleAllOther`}
                type="select"
                mode="dark"
                label="All Peril Deductible"
                disabled={disableRiskForm}
                riskType={riskType}
                state={state}
              />
              <BrandFormField
                xs={4}
                id={`${fieldName}.coverages.deductibleWindHail`}
                name={`${fieldName}.coverages.deductibleWindHail`}
                type="select"
                options={windHailDeductibleOptions}
                mode="dark"
                label="Wind & Hail Deductible"
                disabled={disableRiskForm}
                riskType={riskType}
                coverages={item.coverages}
                state={state}
                coverage={item.coverages?.coverageE}
                description="Wind and hail deductible must match an All Peril when it's value is greater than 2% of coverage E"
              />

              <Grid item xs={12} className={classes.bottomMargin}>
                <Label type="formSubTitle" className={classes.sectionDivider}>
                  Optional Coverages
                </Label>
              </Grid>
              <Field
                xs={6}
                id={`${fieldName}.coverages.structureLossSettlement`}
                name={`${fieldName}.coverages.structureLossSettlement`}
                type="select"
                options={getLossSettlementOptions({
                  isDwellingContents,
                  isHoopBuilding,
                  isFencing,
                  isSolarPanels,
                  isStorageGrain,
                  isConfinementBuilding,
                  isPoultryBuilding,
                  isBarnOutbuilding,
                  buildingClass,
                  session,
                  buildingAge,
                  maxAgeThreshold: buildingMaxAgeThreshold
                })}
                mode="dark"
                label="Structure Loss Settlement"
                disabled={disableRiskForm || isRequiredFieldForClass('structureLossSettlement')}
                helperText={
                  isBarnOutbuilding && buildingClass && (buildingClass === 1 || buildingClass === 2)
                    ? `Required as Replacement Cost for Building Class ${buildingClass}`
                    : undefined
                }
              />
              {/* Roof Loss Settlement is N/A for roofless structures — Fencing, Solar Panels (#33790),
                  and also Hoop Buildings & Silos/Grain Bins (#33841/#33849). */}
              {!isRooflessStructure && (
                <BrandFormField
                  xs={6}
                  id={`${fieldName}.coverages.roofLossSettlement`}
                  name={`${fieldName}.coverages.roofLossSettlement`}
                  type="select"
                  options={roofLossSettlementOptions}
                  // need this key so that the field re-renders when the options change
                  key={`${fieldName}.roofLossSettlement.${buildingClass}.${roofLossSettlementOptions.length}`}
                  mode="dark"
                  label="Roof Loss Settlement"
                  disabled={disableRiskForm}
                  error={!roofLossSettlementValidation.isValid}
                  helperText={roofLossSettlementValidation.message || undefined}
                  riskType={riskType}
                  state={state}
                />
              )}

              {/* Coverage E earthquake deductible (issue #32439). Rendered only for brands that
                  wire deductibleEarthquake in customFormComponentPaths for Fo6Structures; hidden
                  by default via BrandFormField's hiddenCoverages check for brands that don't.
                  #34328: also hidden for Solar Panels and Type 3 barns — earthquake isn't offered
                  for those two items. */}
              {!hideEarthquakeDeductible && (
                <BrandFormField
                  xs={6}
                  id={`${fieldName}.coverages.deductibleEarthquake`}
                  name={`${fieldName}.coverages.deductibleEarthquake`}
                  type="select"
                  mode="dark"
                  label="Earthquake Deductible"
                  disabled={disableRiskForm}
                  riskType={riskType}
                  state={state}
                  zip={address?.zip}
                />
              )}

              <BrandFormField
                name={`${fieldName}.coverages.coverageMS`}
                id={`${fieldName}.coverages.coverageMS`}
                type="select"
                mode="dark"
                xs={6}
                disabled={disableRiskForm}
                riskType={riskType}
                state={state}
                coverages={item.coverages}
                county={address?.county}
                targetCoverage={item.coverages?.coverageE}
                targetCoverageLimit={mineSubsidenceCoverageLimit}
                helperTextLabel="Matches coverage E"
              />
              <BrandFormField
                name={`${fieldName}.coverages.hasActiveCoverageMS`}
                id={`${fieldName}.coverages.hasActiveCoverageMS`}
                type="checkbox"
                label="This structure has active mine subsidence or unrepaired mine damage"
                mode="dark"
                xs={6}
                disabled={disableRiskForm}
                riskType={riskType}
                state={state}
              />

              <Field
                xs={6}
                name={`${fieldName}.coverages.specialFormCoverage`}
                id={`${fieldName}.coverages.specialFormCoverage`}
                type="checkbox"
                label={`Special form coverage${isBarnOutbuilding && buildingClass ? ` (${buildingClass === 1 || buildingClass === 2 ? 'Required' : 'Excluded'} for Building Class ${buildingClass})` : ''}`}
                mode="dark"
                disabled={
                  disableRiskForm ||
                  (isBarnOutbuilding && (buildingClass === 3 || isRequiredFieldForClass('specialFormCoverage'))) ||
                  ((isConfinementBuilding || isPoultryBuilding) && item.coverages?.structureLossSettlement === 'RC') ||
                  (!isBarnOutbuilding && item.coverages?.structureLossSettlement === 'ACV')
                }
                helperText={structureCoverageHelperText({
                  isBarnOutbuilding,
                  isConfinementOrPoultry: isConfinementBuilding || isPoultryBuilding,
                  structureLossSettlement: item.coverages?.structureLossSettlement,
                  buildingClass,
                  class12Text: `Building Class ${buildingClass}: RC + Special Form + Snow/Ice required`,
                  class3Text: `Building Class ${buildingClass}: ACV only, Special Form and Snow/Ice excluded`,
                  fallback: ''
                })}
              />
              <Field
                xs={6}
                name={`${fieldName}.coverages.hasReplacementCostCoverage`}
                id={`${fieldName}.coverages.hasReplacementCostCoverage`}
                type="checkbox"
                label="Replacement cost provision"
                mode="dark"
                disabled={
                  disableRiskForm ||
                  (isBarnOutbuilding &&
                    (buildingClass === 3 || isRequiredFieldForClass('hasReplacementCostCoverage'))) ||
                  ((isConfinementBuilding || isPoultryBuilding) && item.coverages?.structureLossSettlement === 'RC') ||
                  (!isBarnOutbuilding && item.coverages?.structureLossSettlement === 'ACV')
                }
                helperText={structureCoverageHelperText({
                  isBarnOutbuilding,
                  isConfinementOrPoultry: isConfinementBuilding || isPoultryBuilding,
                  structureLossSettlement: item.coverages?.structureLossSettlement,
                  buildingClass,
                  class12Text: `Building Class ${buildingClass}: RC + Special Form + Snow/Ice + Replacement Cost Provision required`,
                  class3Text:
                    'Building Class 3: ACV only, Special Form, Snow/Ice, and Replacement Cost Provision excluded',
                  fallback: undefined
                })}
              />

              <BrandFormField
                xs={6}
                name={`${fieldName}.coverages.repairRebuildRequirement`}
                id={`${fieldName}.coverages.repairRebuildRequirement`}
                type="checkbox"
                label="Repair or rebuilding requirement"
                mode="dark"
                disabled={disableRiskForm}
                riskType={riskType}
                riskFieldName={fieldName}
                state={state}
              />
              <Field
                xs={6}
                name={`${fieldName}.coverages.buildingMaterialTheft`}
                id={`${fieldName}.coverages.buildingMaterialTheft`}
                type="checkbox"
                label="Theft of building materials"
                mode="dark"
                disabled={disableRiskForm}
              />

              <Field
                xs={6}
                name={`${fieldName}.coverages.hasSnowWeightCoverage`}
                id={`${fieldName}.coverages.hasSnowWeightCoverage`}
                type="checkbox"
                label={`Weight of ice, snow, or sleet coverage${isBarnOutbuilding && buildingClass ? ` (${buildingClass === 1 || buildingClass === 2 ? 'Required' : 'Excluded'} for Building Class ${buildingClass})` : ''}`}
                mode="dark"
                disabled={
                  disableRiskForm ||
                  (isBarnOutbuilding && (buildingClass === 3 || isRequiredFieldForClass('hasSnowWeightCoverage'))) ||
                  ((isConfinementBuilding || isPoultryBuilding) && item.coverages?.structureLossSettlement === 'RC') ||
                  (!isBarnOutbuilding && item.coverages?.structureLossSettlement === 'ACV')
                }
                helperText={structureCoverageHelperText({
                  isBarnOutbuilding,
                  isConfinementOrPoultry: isConfinementBuilding || isPoultryBuilding,
                  structureLossSettlement: item.coverages?.structureLossSettlement,
                  buildingClass,
                  class12Text: `Building Class ${buildingClass}: RC + Special Form + Snow/Ice required`,
                  class3Text: `Building Class ${buildingClass}: ACV only, Special Form and Snow/Ice excluded`,
                  fallback: undefined
                })}
              />

              <Grid item xs={12} className={classes.bottomMargin}>
                <Label type="formSubTitle" className={classes.sectionDivider}>
                  Exclusions
                </Label>
              </Grid>
              {/* FO-6 structures are locked "excluded" (the toggle is never editable). A CFM underwriter
                  can tick the UW Override below on an RC structure: it turns the exclusion and all three
                  surfacing types off (all or nothing) and persists cosmeticDamageUwOverride, so the hook's
                  Effect 6 and the offer loader/store keep the structure "Included" (#35739, #35870). */}
              <Field
                xs={6}
                type="switch"
                id={`${fieldName}.coverages.cosmeticDamageExclusion`}
                name={`${fieldName}.coverages.cosmeticDamageExclusion`}
                mode="dark"
                label="Cosmetic damage exclusion"
                disabled
              />
              {riskType === FarmRiskType.Fo6Structures && (
                <CosmeticDamageUwOverride
                  fieldName={`${fieldName}.coverages`}
                  coverages={item.coverages}
                  disabled={disableRiskForm}
                />
              )}

              {item.coverages?.cosmeticDamageExclusion && (
                <>
                  <Grid item xs={12}>
                    <Label type="infoTitleSmall">APPLY COSMETIC DAMAGE EXCLUSION TO THESE SURFACING TYPES:</Label>
                  </Grid>
                  <Field
                    xs={4}
                    type="checkbox"
                    id={`${fieldName}.coverages.exteriorDoorsWindowsDamageExclusion`}
                    name={`${fieldName}.coverages.exteriorDoorsWindowsDamageExclusion`}
                    mode="dark"
                    label="Exterior doors & windows"
                    disabled={disableRiskForm || riskType === FarmRiskType.Fo6Structures}
                  />
                  <Field
                    xs={4}
                    type="checkbox"
                    id={`${fieldName}.coverages.exteriorWallsDamageExclusion`}
                    name={`${fieldName}.coverages.exteriorWallsDamageExclusion`}
                    mode="dark"
                    label="Exterior walls"
                    disabled={disableRiskForm || riskType === FarmRiskType.Fo6Structures}
                  />
                  <Field
                    xs={4}
                    type="checkbox"
                    id={`${fieldName}.coverages.roofsDamageExclusion`}
                    name={`${fieldName}.coverages.roofsDamageExclusion`}
                    mode="dark"
                    label="Roofs"
                    disabled={disableRiskForm || riskType === FarmRiskType.Fo6Structures}
                  />
                </>
              )}
              {/* Roof exclusion/restriction is N/A for roofless structures — Hoop Buildings,
                  Silos/Grain Bins, Solar Panels and Fencing (#33841/#33849). */}
              {!isRooflessStructure && (
                <Field
                  xs={6}
                  type="switch"
                  id={`${fieldName}.coverages.hasRoofExclusionRestriction`}
                  name={`${fieldName}.coverages.hasRoofExclusionRestriction`}
                  mode="dark"
                  label="Roof exclusion/restriction"
                  disabled={disableRiskForm}
                />
              )}

              {!isRooflessStructure && item.coverages?.hasRoofExclusionRestriction && (
                <>
                  <Grid item xs={12}>
                    <Label type="infoTitleSmall">ROOF EXCLUSION/RESTRICTION DETAILS:</Label>
                  </Grid>
                  <Field
                    // #33576: remount when the option set changes (e.g. building-class transition)
                    // so the select never renders a stale value that's no longer in its options.
                    key={`${fieldName}.roofExclusionRestriction.${buildingClass}.${roofExclusionRestrictionOptions.length}`}
                    xs={6}
                    id={`${fieldName}.coverages.roofExclusionRestriction`}
                    name={`${fieldName}.coverages.roofExclusionRestriction`}
                    type="select"
                    options={roofExclusionRestrictionOptions}
                    mode="dark"
                    label="Roof exclusion/Restriction"
                    disabled={disableRiskForm}
                  />
                  <Field
                    xs={12}
                    id={`${fieldName}.coverages.roofExclusionDescription`}
                    name={`${fieldName}.coverages.roofExclusionDescription`}
                    type="textarea"
                    mode="dark"
                    label="Description of reason for excluding the selected items (required)"
                    rows={3}
                    disabled={disableRiskForm}
                  />
                </>
              )}

              <ExteriorWallSurfacingExclusionSection fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />

              {/* Unscheduled personal property intentionally omitted on Farm FO6 risks (#33802):
                  the money/securities/jewelry/silverware/guns/business-property sublimits are a
                  dwelling-contents concept and don't belong on a farm structure (e.g. a poultry
                  Coverage E barn). Removed for every FO6 risk across all mutuals. */}

              {/* scheduled personal property (inland marine coverage) */}
              {availableCoverageTypes.includes(FarmScheduleCoverageType.SPP) && (
                <InlandMarineForm
                  fieldName={`${fieldName}.coverages.scheduleCoverages`}
                  disabled={disableRiskForm}
                  state={state}
                  riskType={FarmRiskType.Fo6Structures}
                  coverages={riskCoverages}
                />
              )}

              {/* Related Private Structures */}
              {availableCoverageTypes.includes(FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES) && (
                <RelatedPrivateStructuresForm
                  fieldName={`${fieldName}.coverages.scheduleCoverages`}
                  disabled={disableRiskForm}
                  address={locationAddress}
                  riskType={FarmRiskType.Fo6Structures}
                  coverages={riskCoverages}
                />
              )}

              {/* Farm Income Coverage - Hide for Solar Panels, Fencing, Dwelling/Contents, and the
                  non-farm Home (Z) / Rental (Y) products (Farm Income is farm-only, #32133) */}
              {!isSolarPanels &&
                !isFencing &&
                !isDwellingContents &&
                !isHomeOrRentalProduct &&
                availableCoverageTypes.includes(FarmScheduleCoverageType.INCOME) && (
                  <FarmIncomeCoverageForm
                    fieldName={`${fieldName}.coverages.scheduleCoverages`}
                    disabled={disableRiskForm}
                    variant="E"
                  />
                )}
            </>
          )}
        </Grid>
      )}
    </CollapsibleCard>
  );
};

FO6.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  fieldName: PropTypes.string.isRequired,
  canRemoveRisk: PropTypes.bool.isRequired,
  removeRisk: PropTypes.func.isRequired,
  setRiskFieldnameToEdit: PropTypes.func.isRequired,
  locationAddress: PropTypes.object.isRequired
};

export default observer(withDatePicker(FO6));
